import { useCallback, useContext, useEffect, useState } from 'react';
import { IApiHooksWithTriggerData, IIdentityObject } from '../../interfaces/app-interfaces';
import { ICaseDetailsAPIResponse, ICaseDetailsRequestData, IChatCaseData, IChatQueueType } from '../../interfaces/case-details-api-interfaces';
import { useIdentity } from '@vp/auth-react';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { API_ENDPOINTS } from '../../config/api-config';
import { transformChatContextResponse } from '../../utils/chat/chat-case-transform-utils';
import { CAContext } from '../../contexts/CAContext';
import { getRestId } from '../../utils/session-storage-utils';
import { getQueueFromPageName } from '../../utils/miaw/init-chat-utils';
import { isMobileOrTablet } from '../../utils/mobile-utils';

const useChatCaseDetails = (): IApiHooksWithTriggerData<{ caseData: IChatCaseData; queueInfo: IChatQueueType }, ICaseDetailsRequestData> => {
  const {
    chatState: { locale },
  } = useContext(CAContext);
  const identity = useIdentity();
  const { logCustomAttributesNR } = useNRLogging();
  const [data, setData] = useState<{ caseData: IChatCaseData; queueInfo: IChatQueueType }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [requestData, setRequestData] = useState<ICaseDetailsRequestData>();

  const trigger = useCallback((chatDetails: ICaseDetailsRequestData) => {
    setRequestData(chatDetails);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (requestData) {
        try {
          setLoading(true);
          const { authorizationHeader } = identity as IIdentityObject;
          const myHeaders = new Headers();
          myHeaders.append('Authorization', authorizationHeader || '');
          myHeaders.append('Content-Type', 'application/json');

          const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: JSON.stringify(requestData),
            redirect: 'follow' as RequestRedirect,
          };
          logCustomAttributesNR('ca-chatbot-misc-api-request', {
            logLevel: 'info',
            message: 'Getting Chat Case Details',
            requestData: JSON.stringify(requestData),
            apiType: 'getChatContext',
          });
          const requestURL = new URL(`${API_ENDPOINTS.CONCORD_BASE_URL()}/context-builder`);
          requestURL.searchParams.append('request_id', getRestId());

          fetch(requestURL, requestOptions)
            .then((response) => {
              if (!response.ok) {
                const error = new Error(`API responded with status: ${response.status}`);
                error.name = 'ApiError';
                throw error;
              }
              return response.json();
            })
            .then((result) => {
              console.log(result);
              const { caseData, queueInfo } = transformChatContextResponse(result as ICaseDetailsAPIResponse);
              setData({ caseData, queueInfo });
              setError(undefined);
            })
            .catch((error) => {
              setError(error as Error);
              console.error(error);
              /** Add fallback case data in case of error
               * queue mapping based on pageName and pageURL
               * caseOrigin will have default format even in error scenario with a suffix CMIAW
               * priority will be handled based on queue type, if checkout queue then high will be set else medium
               */
              setData({
                caseData: {
                  banner: 'VP',
                  brand: 'VP',
                  caseOrigin: `VistaPrint-ChatAnywhere-${isMobileOrTablet() ? 'Mobile-' : ''}${locale?.toUpperCase()}-${requestData.pageName}-CMIAW`,
                  chatLocale: locale,
                  pageName: requestData.pageName,
                  pageSection: requestData.pageSection,
                  pageStage: requestData.pageStage,
                  pagePath: window?.location.pathname,
                  pageURL: window?.location.href,
                  category: '#Others',
                  subCategory: '#Others',
                  description: `${requestData.pageName} Case`,
                  priority: 'Medium',
                  status: 'In Progress',
                  subject: `Chat - ${requestData.pageName}`,
                },
                queueInfo: getQueueFromPageName(requestData.pageName),
              });
              logCustomAttributesNR('ca-chatbot-misc-api-error', {
                errorName: error?.name,
                errorStack: error?.stack,
                message: error?.message,
                logLevel: 'error',
                apiType: 'getChatContext',
              });
            })
            .finally(() => {
              // Reset loading state and requestData after completion
              setLoading(false);
              setRequestData(undefined);
            });
        } catch (err) {
          setError(err as Error);
        }
      }
    };
    fetchData();
  }, [requestData]);

  return {
    data,
    loading,
    error,
    trigger,
  };
};

export { useChatCaseDetails };
